import type {
  CommandContext,
  CommandReceipt,
  ExceptionCase,
  OperatingSignal,
  SupportCase,
  WorkspaceCommand,
  WorkspaceState,
} from "./types";
import { DomainError } from "./types";
import { redactSupportText } from "./supportAutomation";
import { addDaysIso, upcomingOccurrences } from "./time";
import { maintainWorkspaceSchedule } from "./schedule";

function clone(state: WorkspaceState): WorkspaceState {
  return structuredClone(state);
}

function requireOwnerLike(context: CommandContext): void {
  if (
    !["owner", "manager", "sample_user"].includes(context.actorRole)
  ) {
    throw new DomainError(
      "FORBIDDEN",
      "이 작업을 처리할 권한이 없어요. 원장에게 요청해 주세요.",
    );
  }
}

function requireBillingOperator(context: CommandContext): void {
  if (
    !["owner", "manager", "accounting", "sample_user"].includes(
      context.actorRole,
    )
  ) {
    throw new DomainError(
      "FORBIDDEN",
      "수납 권한이 있는 구성원만 변경할 수 있어요.",
    );
  }
}

function requireAssignedSession(
  context: CommandContext,
  session: WorkspaceState["sessions"][number],
): void {
  if (
    context.actorRole === "instructor" &&
    !(context.scopeClassIds ?? []).includes(session.classId)
  ) {
    throw new DomainError(
      "FORBIDDEN",
      "배정된 수업의 기록만 변경할 수 있어요. 대상 정보는 공개하지 않았습니다.",
    );
  }
}

function requireOwner(context: CommandContext): void {
  if (!["owner", "sample_user"].includes(context.actorRole)) {
    throw new DomainError(
      "FORBIDDEN",
      "원장만 변경할 수 있어요. 대상 정보는 공개하지 않았습니다.",
    );
  }
}

function normalized(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLocaleLowerCase("ko-KR");
}

function timeOverlaps(
  startA: string,
  endA: string,
  startB: string,
  endB: string,
): boolean {
  return startA < endB && startB < endA;
}

function requirePositiveIntegerKrw(value: number, field: string): void {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new DomainError(
      "INVALID_MONEY",
      "금액은 1원 이상의 정수로 입력해 주세요.",
      false,
      {
        [field]: "1원 이상의 원 단위 금액을 입력해 주세요.",
      },
    );
  }
}

function refreshInvoiceState(invoice: WorkspaceState["invoices"][number]): void {
  invoice.balanceKrw =
    invoice.amountKrw -
    invoice.discountKrw -
    invoice.paidKrw +
    invoice.refundedKrw;
  invoice.state =
    invoice.balanceKrw === 0
      ? "paid"
      : invoice.balanceKrw < 0
        ? "overpaid"
        : invoice.paidKrw > 0
          ? "partial"
          : invoice.refundedKrw > 0
            ? "refunded"
            : "issued";
}

function priority(exception: ExceptionCase): number {
  const severity = { critical: 0, high: 10, normal: 20, low: 30 }[
    exception.severity
  ];
  const kind = {
    security: 0,
    billing: 1,
    guardian: 2,
    attendance: 3,
    makeup: 4,
    payroll: 5,
    support: 6,
  }[exception.kind];
  return severity + kind;
}

export function unresolvedExceptions(state: WorkspaceState): ExceptionCase[] {
  return state.exceptions
    .filter((item) =>
      ["open", "proposed", "awaiting_confirmation"].includes(item.status),
    )
    .sort(
      (a, b) =>
        priority(a) - priority(b) ||
        a.dueAt.localeCompare(b.dueAt) ||
        a.id.localeCompare(b.id),
    );
}

export function deriveSignals(
  state: WorkspaceState,
  now: string,
): OperatingSignal[] {
  const nowMs = Date.parse(now);
  const since = (value: string | null, days: number) =>
    value !== null && Date.parse(value) >= nowMs - days * 86_400_000;
  const marked = state.attendance.filter(
    (item) => item.status !== "unmarked" && since(item.recordedAt, 30),
  );
  const absent = marked.filter((item) => item.status === "absent");
  const unbooked = state.makeups.filter(
    (item) => item.eligibility === "eligible" && item.bookedSessionId === null,
  );
  const unpaid = state.invoices.filter((invoice) => invoice.balanceKrw > 0);
  const recentDailyCloses = state.closeSnapshots.filter(
    (snapshot) =>
      snapshot.period === "day" &&
      snapshot.reopenedAt === null &&
      since(snapshot.completedAt, 30),
  );
  const attendanceById = new Map(
    state.attendance.map((item) => [item.id, item]),
  );
  const makeups90 = state.makeups.filter((item) =>
    since(attendanceById.get(item.sourceAttendanceId)?.recordedAt ?? null, 90),
  );
  const bookedMakeups90 = makeups90.filter((item) => item.bookedSessionId);
  const sessions365 = state.sessions.filter(
    (session) =>
      session.state === "completed" &&
      Date.parse(session.endsAt) >= nowMs - 365 * 86_400_000 &&
      Date.parse(session.endsAt) <= nowMs,
  );
  const activeMonths365 = new Set(
    sessions365.map((session) => session.endsAt.slice(0, 7)),
  );

  return [
    {
      id: "signal-absence-rate",
      title: "결석 흐름",
      state:
        marked.length < 5
          ? "unknown"
          : absent.length / marked.length >= 0.15
            ? "observed"
            : "healthy",
      numerator: absent.length,
      denominator: marked.length,
      period: "최근 30일",
      explanation:
        marked.length < 5
          ? "출결 표본이 5건보다 적어 아직 판단할 수 없어요."
          : `기록된 출결 ${marked.length}건 중 결석이 ${absent.length}건이에요.`,
      sourceIds: marked.map((item) => item.id),
      staleAt: now,
    },
    {
      id: "signal-makeup-open",
      title: "남은 보강 후보",
      state: unbooked.length > 0 ? "observed" : "healthy",
      numerator: unbooked.length,
      denominator: state.makeups.length,
      period: "현재 유효한 후보",
      explanation:
        unbooked.length > 0
          ? `예약하지 않은 보강 후보가 ${unbooked.length}건 있어요.`
          : "현재 남은 보강 후보가 없어요.",
      sourceIds: unbooked.map((item) => item.id),
      staleAt: now,
    },
    {
      id: "signal-unpaid",
      title: "수납 차이",
      state: unpaid.length > 0 ? "observed" : "healthy",
      numerator: unpaid.length,
      denominator: state.invoices.length,
      period: "현재 청구 기간",
      explanation:
        unpaid.length > 0
          ? `잔액이 남은 청구가 ${unpaid.length}건 있어요.`
          : "대조할 수납 차이가 없어요.",
      sourceIds: unpaid.map((item) => item.id),
      staleAt: now,
    },
    {
      id: "signal-close-history-30d",
      title: "30일 마감 이력",
      state: recentDailyCloses.length === 0 ? "unknown" : "healthy",
      numerator: recentDailyCloses.length,
      denominator: 30,
      period: "최근 30일",
      explanation:
        recentDailyCloses.length === 0
          ? "최근 30일에 완료 상태인 일일 마감이 없어 판단할 수 없어요."
          : `최근 30일에 다시 열리지 않은 일일 마감 ${recentDailyCloses.length}건이 있어요. 운영일 수와 같다고 추정하지 않습니다.`,
      sourceIds: recentDailyCloses.map((item) => item.id),
      staleAt: now,
    },
    {
      id: "signal-makeup-90d",
      title: "90일 보강 연결",
      state:
        makeups90.length < 3
          ? "unknown"
          : bookedMakeups90.length / makeups90.length < 0.7
            ? "observed"
            : "healthy",
      numerator: bookedMakeups90.length,
      denominator: makeups90.length,
      period: "최근 90일",
      explanation:
        makeups90.length < 3
          ? "최근 90일 보강 후보가 3건보다 적어 비율을 판단하지 않아요."
          : `최근 90일 보강 후보 ${makeups90.length}건 중 ${bookedMakeups90.length}건에 수업이 연결됐어요.`,
      sourceIds: makeups90.map((item) => item.id),
      staleAt: now,
    },
    {
      id: "signal-history-365d",
      title: "1년 운영 이력",
      state: sessions365.length < 8 ? "unknown" : "healthy",
      numerator: activeMonths365.size,
      denominator: 12,
      period: "최근 365일",
      explanation:
        sessions365.length < 8
          ? "최근 365일 완료 수업이 8건보다 적어 장기 변화를 판단하지 않아요."
          : `최근 365일 완료 수업 ${sessions365.length}건이 ${activeMonths365.size}개 월에 걸쳐 있어요. 성과나 유지율로 해석하지 않습니다.`,
      sourceIds: sessions365.map((item) => item.id),
      staleAt: now,
    },
  ];
}

function finish(
  state: WorkspaceState,
  command: WorkspaceCommand,
  context: CommandContext,
  changedIds: string[],
  summary: string,
): { state: WorkspaceState; receipt: CommandReceipt } {
  state.version += 1;
  state.generatedAt = context.now;
  state.signals = deriveSignals(state, context.now);
  state.auditLog ??= [];
  state.auditLog.push({
    id: `audit-${context.eventId}`,
    action: command.type,
    actorId: context.actorId,
    targetIds: [...changedIds],
    summary,
    occurredAt: context.now,
  });
  return {
    state,
    receipt: {
      eventId: context.eventId,
      eventType: command.type,
      version: state.version,
      changedIds,
      nextActions: unresolvedExceptions(state)
        .slice(0, 3)
        .map((item) => item.id),
      summary,
      occurredAt: context.now,
    },
  };
}

export function applyCommand(
  current: WorkspaceState,
  command: WorkspaceCommand,
  context: CommandContext,
): { state: WorkspaceState; receipt: CommandReceipt } {
  const state = clone(current);
  state.teamMembers ??= [];
  state.importReceipts ??= [];
  state.dataRequests ??= [];
  state.auditLog ??= [];
  const changed: string[] = maintainWorkspaceSchedule(state, context.now);

  switch (command.type) {
    case "attendance.mark": {
      if (
        !["owner", "manager", "instructor", "desk", "sample_user"].includes(
          context.actorRole,
        )
      ) {
        throw new DomainError(
          "FORBIDDEN",
          "배정된 수업의 출결만 기록할 수 있어요.",
        );
      }
      const session = state.sessions.find(
        (item) => item.id === command.sessionId,
      );
      const student = state.students.find(
        (item) => item.id === command.studentId,
      );
      const attendance = state.attendance.find(
        (item) =>
          item.sessionId === command.sessionId &&
          item.studentId === command.studentId,
      );
      if (!session || !student || !attendance)
        throw new DomainError(
          "NOT_FOUND",
          "수업 또는 학생 기록을 찾지 못했어요. 새로고침한 뒤 다시 시도해 주세요.",
        );
      const className =
        state.classes.find((item) => item.id === session.classId)?.name ??
        "오늘 수업";
      requireAssignedSession(context, session);
      if (
        !state.classes
          .find((item) => item.id === session.classId)
          ?.studentIds.includes(student.id)
      ) {
        throw new DomainError(
          "STUDENT_NOT_ENROLLED",
          "이 수업 명단에 없는 학생이에요.",
        );
      }

      const previous = attendance.status;
      attendance.status = command.status;
      if (command.note === undefined) delete attendance.note;
      else attendance.note = command.note;
      attendance.recordedAt = context.now;
      attendance.syncState = "synced";
      attendance.version += 1;
      changed.push(attendance.id);

      if (previous === command.status)
        return finish(
          state,
          command,
          context,
          changed,
          "이미 같은 출결 상태로 기록되어 있어요.",
        );

      const desiredDelta =
        command.status === "present" ||
        command.status === "late" ||
        command.status === "left_early"
          ? -1
          : 0;
      const currentDelta = state.ledger
        .filter(
          (item) =>
            item.studentId === student.id && item.sessionId === session.id,
        )
        .reduce((sum, item) => sum + item.delta, 0);
      const correctionDelta =
        student.billingPlan === "session" ? desiredDelta - currentDelta : 0;
      const ledgerId = `ledger-${context.eventId}`;
      if (student.billingPlan === "session") {
        const priorBalance = student.remainingSessions ?? 0;
        const balanceAfter = priorBalance + correctionDelta;
        if (balanceAfter < 0)
          throw new DomainError(
            "NO_SESSION_BALANCE",
            "남은 회차가 없어 차감하지 않았어요. 수납 기록을 먼저 확인해 주세요.",
          );
        student.remainingSessions = balanceAfter;
        state.ledger.push({
          id: ledgerId,
          studentId: student.id,
          sessionId: session.id,
          kind:
            correctionDelta === -1
              ? "consume"
              : correctionDelta === 1
                ? "restore"
                : "correction",
          delta: correctionDelta,
          balanceAfter,
          reason:
            currentDelta === desiredDelta
              ? `${previous}에서 ${command.status}로 변경 · 회차 영향 없음`
              : `${previous}에서 ${command.status}로 변경 · 이전 원장 합계 ${currentDelta}회에서 ${desiredDelta}회로 보정`,
          occurredAt: context.now,
        });
      } else {
        state.ledger.push({
          id: ledgerId,
          studentId: student.id,
          sessionId: session.id,
          kind: "monthly_attendance",
          delta: 0,
          balanceAfter: null,
          reason: `${command.status} 월정액 출결 기록`,
          occurredAt: context.now,
        });
      }
      changed.push(ledgerId);

      if (previous === "absent" && command.status !== "absent") {
        for (const candidate of state.makeups.filter(
          (item) =>
            item.sourceAttendanceId === attendance.id &&
            item.bookedSessionId === null,
        )) {
          candidate.eligibility = "ineligible";
          candidate.reason = "출결이 결석에서 변경되어 보강 후보를 닫았어요.";
          candidate.revision += 1;
          changed.push(candidate.id);
        }
        for (const exception of state.exceptions.filter(
          (item) =>
            item.studentId === student.id &&
            item.kind === "attendance" &&
            ["open", "proposed", "awaiting_confirmation"].includes(item.status),
        )) {
          exception.status = "cancelled";
          exception.revision += 1;
          changed.push(exception.id);
        }
      }

      if (command.status === "absent") {
        const makeupId = `makeup-${context.eventId}`;
        const guardianId = `guardian-${context.eventId}`;
        const exceptionId = `exception-${context.eventId}`;
        state.makeups.push({
          id: makeupId,
          studentId: student.id,
          sourceAttendanceId: attendance.id,
          eligibility: "eligible",
          reason: "당일 수업 시작 전 연락된 결석으로 샘플 보강 규칙에 맞아요.",
          suggestedSessionIds: state.sessions
            .filter(
              (item) => item.id !== session.id && item.state === "scheduled",
            )
            .slice(0, 3)
            .map((item) => item.id),
          bookedSessionId: null,
          expiresAt: addDaysIso(context.now, 30),
          revision: 1,
        });
        state.guardianDrafts.push({
          id: guardianId,
          studentId: student.id,
          purpose: "makeup",
          body: `${student.displayName} 학생의 오늘 결석을 기록했습니다. 보강 가능한 시간을 확인해 주세요.`,
          recipientMasked: student.guardianContactMasked,
          receiptState: "draft",
          requiresReview: true,
          quietHoursBlocked: false,
          capabilityId: null,
          updatedAt: context.now,
        });
        state.exceptions.push({
          id: exceptionId,
          kind: "attendance",
          title: `${student.displayName} 결석 뒤 할 일`,
          summary: "보강 후보와 보호자 안내 초안이 함께 준비됐어요.",
          studentId: student.id,
          sourceEventId: context.eventId,
          status: "open",
          severity: "normal",
          cause: `${student.displayName} 학생을 ${className} 수업에서 결석으로 기록했어요.`,
          rule: "당일 연락 결석 · 회차 차감 없음 · 30일 안에 보강 후보 생성",
          impact:
            "수강 횟수, 보강 후보, 보호자 안내 초안이 이 결석 기록에 함께 연결됐어요.",
          suggestedAction:
            "보강 후보로 남기고 보호자에게 보내기 전 내용을 확인하세요.",
          dueAt: context.now,
          revision: 1,
        });
        changed.push(makeupId, guardianId, exceptionId);
      }

      return finish(
        state,
        command,
        context,
        changed,
        `${student.displayName} 학생을 ${command.status === "absent" ? "결석" : "출석 상태"}으로 기록했어요.`,
      );
    }

    case "attendance.bulkPresent": {
      if (
        !["owner", "manager", "instructor", "desk", "sample_user"].includes(
          context.actorRole,
        )
      ) {
        throw new DomainError(
          "FORBIDDEN",
          "배정된 수업의 출결만 기록할 수 있어요.",
        );
      }
      const session = state.sessions.find(
        (item) => item.id === command.sessionId,
      );
      if (!session) throw new DomainError("NOT_FOUND", "수업을 찾지 못했어요.");
      requireAssignedSession(context, session);
      const records = state.attendance.filter(
        (item) => item.sessionId === session.id && item.status === "unmarked",
      );
      let skipped = 0;
      for (const record of records) {
        const student = state.students.find(
          (item) => item.id === record.studentId,
        );
        if (
          student?.billingPlan === "session" &&
          (student.remainingSessions ?? 0) <= 0
        ) {
          const exceptionId = `exception-balance-${context.eventId}-${student.id}`;
          state.exceptions.push({
            id: exceptionId,
            kind: "billing",
            title: `${student.displayName} 남은 회차 확인`,
            summary: "남은 회차가 0이라 일괄 출석에서 제외했어요.",
            studentId: student.id,
            sourceEventId: context.eventId,
            status: "open",
            severity: "high",
            cause: "회차제 학생의 현재 원장 잔액이 0회예요.",
            rule: "0회 아래로 자동 차감하지 않음",
            impact:
              "다른 학생은 저장하고 이 학생의 출결은 기록 전으로 유지했어요.",
            suggestedAction:
              "수납·회차 원장을 확인한 뒤 개별 출결을 기록하세요.",
            dueAt: context.now,
            revision: 1,
          });
          changed.push(exceptionId);
          skipped += 1;
          continue;
        }
        record.status = "present";
        record.recordedAt = context.now;
        record.syncState = "synced";
        record.version += 1;
        if (
          student?.billingPlan === "session" &&
          (student.remainingSessions ?? 0) > 0
        )
          student.remainingSessions = (student.remainingSessions ?? 0) - 1;
        state.ledger.push({
          id: `ledger-${context.eventId}-${record.studentId}`,
          studentId: record.studentId,
          sessionId: session.id,
          kind:
            student?.billingPlan === "session"
              ? "consume"
              : "monthly_attendance",
          delta: student?.billingPlan === "session" ? -1 : 0,
          balanceAfter: student?.remainingSessions ?? null,
          reason: "반 전체 정상 출석 처리",
          occurredAt: context.now,
        });
        changed.push(record.id);
      }
      return finish(
        state,
        command,
        context,
        changed,
        `${records.length - skipped}명을 출석으로 기록했어요.${skipped ? ` 회차 확인이 필요한 ${skipped}명은 기록 전으로 남겼습니다.` : " 예외 학생만 바꿔 주세요."}`,
      );
    }

    case "exception.resolve": {
      requireOwnerLike(context);
      const exception = state.exceptions.find(
        (item) => item.id === command.exceptionId,
      );
      if (!exception)
        throw new DomainError("NOT_FOUND", "처리할 항목을 찾지 못했어요.");
      if (exception.status === "resolved")
        return finish(
          state,
          command,
          context,
          [exception.id],
          "이미 처리된 항목이에요.",
        );
      if (exception.severity === "critical") {
        throw new DomainError(
          "CRITICAL_REQUIRES_RECEIPT",
          "중요 항목은 전용 확인 영수증 없이 일반 마감에서 완료할 수 없어요.",
        );
      }
      exception.status = "resolved";
      exception.resolvedAt = context.now;
      exception.revision += 1;
      const relatedDraft = state.guardianDrafts.find(
        (draft) =>
          draft.studentId === exception.studentId &&
          draft.receiptState === "draft",
      );
      if (relatedDraft) {
        relatedDraft.requiresReview = false;
        relatedDraft.updatedAt = context.now;
        changed.push(relatedDraft.id);
      }
      const effectId = `effect-${context.eventId}`;
      state.effectReceipts.push({
        id: effectId,
        effectType: "undo",
        subjectId: exception.id,
        state: "accepted",
        provider: "product",
        summary: "결정이 저장됐어요. 10분 안에 다시 열 수 있어요.",
        occurredAt: context.now,
      });
      changed.push(exception.id, effectId);
      return finish(
        state,
        command,
        context,
        changed,
        `${exception.title}을 처리했어요.`,
      );
    }

    case "exception.defer": {
      requireOwnerLike(context);
      if (command.reason.trim().length < 4)
        throw new DomainError(
          "REASON_REQUIRED",
          "보류 이유를 4자 이상 적어 주세요.",
          false,
          { reason: "다음 사람이 이해할 수 있는 이유가 필요해요." },
        );
      const exception = state.exceptions.find(
        (item) => item.id === command.exceptionId,
      );
      if (!exception)
        throw new DomainError("NOT_FOUND", "보류할 항목을 찾지 못했어요.");
      if (exception.severity === "critical")
        throw new DomainError(
          "CRITICAL_CANNOT_DEFER",
          "데이터 손실·보안·오발송 위험은 보류할 수 없어요.",
        );
      exception.status = "deferred";
      exception.deferReason = command.reason.trim();
      exception.revision += 1;
      return finish(
        state,
        command,
        context,
        [exception.id],
        `${exception.title}을 이유와 함께 보류했어요.`,
      );
    }

    case "makeup.book": {
      requireOwnerLike(context);
      const candidate = state.makeups.find(
        (item) => item.id === command.makeupId,
      );
      const target = state.sessions.find(
        (item) => item.id === command.sessionId,
      );
      if (!candidate || !target)
        throw new DomainError(
          "NOT_FOUND",
          "보강 후보 또는 수업을 찾지 못했어요.",
        );
      if (candidate.eligibility !== "eligible")
        throw new DomainError(
          "MAKEUP_INELIGIBLE",
          `보강을 확정할 수 없어요: ${candidate.reason}`,
        );
      if (Date.parse(candidate.expiresAt) <= Date.parse(context.now))
        throw new DomainError(
          "MAKEUP_EXPIRED",
          "보강 신청 기한이 지나 예약하지 않았어요.",
        );
      if (candidate.bookedSessionId)
        throw new DomainError(
          "MAKEUP_ALREADY_BOOKED",
          "이미 보강 수업이 연결되어 있어요. 먼저 기존 예약을 취소해 주세요.",
        );
      const group = state.classes.find((item) => item.id === target.classId);
      if (!group || group.studentIds.length >= group.capacity)
        throw new DomainError(
          "CAPACITY_FULL",
          "마지막 자리가 이미 찼어요. 다른 시간을 선택해 주세요.",
          true,
        );
      candidate.bookedSessionId = target.id;
      candidate.revision += 1;
      return finish(
        state,
        command,
        context,
        [candidate.id],
        "보강 시간을 후보에 연결했어요. 보호자 응답 뒤 원장이 최종 확인합니다.",
      );
    }

    case "makeup.cancel": {
      requireOwnerLike(context);
      const candidate = state.makeups.find(
        (item) => item.id === command.makeupId,
      );
      if (!candidate)
        throw new DomainError("NOT_FOUND", "보강 후보를 찾지 못했어요.");
      if (!candidate.bookedSessionId)
        throw new DomainError(
          "MAKEUP_NOT_BOOKED",
          "취소할 보강 예약이 없어요.",
        );
      candidate.bookedSessionId = null;
      candidate.eligibility =
        Date.parse(candidate.expiresAt) > Date.parse(context.now)
          ? "eligible"
          : "ineligible";
      candidate.reason =
        candidate.eligibility === "eligible"
          ? `예약 취소 · ${command.reason.trim()}`
          : `예약 취소 뒤 신청 기한 만료 · ${command.reason.trim()}`;
      candidate.revision += 1;
      return finish(
        state,
        command,
        context,
        [candidate.id],
        "보강 예약을 이유와 함께 취소했어요. 원래 후보 기록은 남겨 둡니다.",
      );
    }

    case "learning.save": {
      if (
        !["owner", "manager", "instructor", "sample_user"].includes(
          context.actorRole,
        )
      )
        throw new DomainError(
          "FORBIDDEN",
          "수업을 맡은 강사만 학습 기록을 남길 수 있어요.",
        );
      const session = state.sessions.find(
        (item) => item.id === command.sessionId,
      );
      if (!session)
        throw new DomainError(
          "NOT_FOUND",
          "수업 기록을 찾지 못했어요. 새로고침한 뒤 다시 시도해 주세요.",
        );
      requireAssignedSession(context, session);
      if (command.studentId) {
        const classGroup = state.classes.find(
          (item) => item.id === session.classId,
        );
        if (!classGroup?.studentIds.includes(command.studentId))
          throw new DomainError(
            "NOT_FOUND",
            "이 수업에서 학생 기록을 찾지 못했어요.",
          );
      }
      if (
        command.progress.trim().length === 0 &&
        command.homework.trim().length === 0
      )
        throw new DomainError(
          "EMPTY_NOTE",
          "진도나 과제 중 하나는 적어 주세요.",
        );
      const existing = state.learningNotes.find(
        (item) =>
          item.sessionId === command.sessionId &&
          item.studentId === (command.studentId ?? null),
      );
      if (existing) {
        existing.progress = command.progress.trim();
        existing.homework = command.homework.trim();
        existing.visibility = command.visibility;
        existing.revision += 1;
        existing.updatedAt = context.now;
        changed.push(existing.id);
      } else {
        const id = `learning-${context.eventId}`;
        state.learningNotes.push({
          id,
          sessionId: command.sessionId,
          studentId: command.studentId ?? null,
          progress: command.progress.trim(),
          homework: command.homework.trim(),
          visibility: command.visibility,
          revision: 1,
          updatedAt: context.now,
        });
        changed.push(id);
      }
      return finish(
        state,
        command,
        context,
        changed,
        command.visibility === "guardian"
          ? "보호자에게 보일 학습 기록으로 저장했어요."
          : "강사와 원장만 보는 기록으로 저장했어요.",
      );
    }

    case "billing.invoiceIssue": {
      requireBillingOperator(context);
      requirePositiveIntegerKrw(command.amountKrw, "amountKrw");
      if (
        !Number.isSafeInteger(command.discountKrw) ||
        command.discountKrw < 0 ||
        command.discountKrw > command.amountKrw
      )
        throw new DomainError(
          "INVALID_DISCOUNT",
          "할인은 0원 이상이며 청구액보다 클 수 없어요.",
        );
      const student = state.students.find(
        (item) => item.id === command.studentId && item.active,
      );
      if (!student)
        throw new DomainError("NOT_FOUND", "학생을 찾지 못했어요.");
      if (
        state.invoices.some(
          (invoice) =>
            invoice.studentId === student.id && invoice.period === command.period,
        )
      )
        throw new DomainError(
          "INVOICE_DUPLICATE",
          `${command.period} 청구가 이미 있어 중복 발행하지 않았어요.`,
        );
      const id = `invoice-${context.eventId}`;
      state.invoices.push({
        id,
        studentId: student.id,
        period: command.period,
        amountKrw: command.amountKrw,
        discountKrw: command.discountKrw,
        paidKrw: 0,
        refundedKrw: 0,
        balanceKrw: command.amountKrw - command.discountKrw,
        state: "issued",
        dueAt: command.dueAt,
      });
      return finish(
        state,
        command,
        context,
        [id],
        `${student.displayName} 학생의 ${command.period} 청구 ${command.amountKrw.toLocaleString("ko-KR")}원을 발행했어요.`,
      );
    }

    case "billing.payment": {
      requireBillingOperator(context);
      requirePositiveIntegerKrw(command.amountKrw, "amountKrw");
      const invoice = state.invoices.find(
        (item) => item.id === command.invoiceId,
      );
      if (!invoice) throw new DomainError("NOT_FOUND", "청구를 찾지 못했어요.");
      if (
        command.kind === "refund" &&
        command.amountKrw > invoice.paidKrw - invoice.refundedKrw
      )
        throw new DomainError(
          "REFUND_TOO_LARGE",
          "실제 입금으로 남아 있는 금액보다 많이 환불할 수 없어요.",
        );
      if (command.kind === "payment") invoice.paidKrw += command.amountKrw;
      else invoice.refundedKrw += command.amountKrw;
      refreshInvoiceState(invoice);
      const receiptId = `payment-${context.eventId}`;
      state.paymentReceipts.push({
        id: receiptId,
        invoiceId: invoice.id,
        kind: command.kind,
        amountKrw: command.amountKrw,
        source: "manual",
        occurredAt: context.now,
      });
      changed.push(invoice.id, receiptId);
      return finish(
        state,
        command,
        context,
        changed,
        `${command.kind === "payment" ? "입금" : "환불"} ${command.amountKrw.toLocaleString("ko-KR")}원을 출처와 함께 기록했어요.`,
      );
    }

    case "billing.paymentReverse": {
      requireBillingOperator(context);
      if (command.reason.trim().length < 4)
        throw new DomainError(
          "REASON_REQUIRED",
          "되돌리는 이유를 4자 이상 적어 주세요.",
        );
      const original = state.paymentReceipts.find(
        (item) => item.id === command.receiptId,
      );
      if (!original)
        throw new DomainError("NOT_FOUND", "입금 영수증을 찾지 못했어요.");
      if (original.kind === "reversal" || original.reversedById)
        throw new DomainError(
          "ALREADY_REVERSED",
          "이미 되돌렸거나 되돌림 자체인 영수증이에요.",
        );
      const invoice = state.invoices.find(
        (item) => item.id === original.invoiceId,
      );
      if (!invoice)
        throw new DomainError("NOT_FOUND", "연결된 청구를 찾지 못했어요.");
      if (original.kind === "payment") invoice.paidKrw -= original.amountKrw;
      else invoice.refundedKrw -= original.amountKrw;
      if (invoice.paidKrw < 0 || invoice.refundedKrw < 0)
        throw new DomainError(
          "LEDGER_CORRUPT",
          "원장 합계가 맞지 않아 자동으로 되돌리지 않았어요.",
        );
      refreshInvoiceState(invoice);
      const reversalId = `payment-reversal-${context.eventId}`;
      original.reversedById = reversalId;
      state.paymentReceipts.push({
        id: reversalId,
        invoiceId: invoice.id,
        kind: "reversal",
        amountKrw: original.amountKrw,
        source: "manual",
        occurredAt: context.now,
        reversesReceiptId: original.id,
      });
      return finish(
        state,
        command,
        context,
        [invoice.id, original.id, reversalId],
        `${original.kind === "payment" ? "입금" : "환불"} 영수증을 이유와 함께 되돌렸어요: ${command.reason.trim()}`,
      );
    }

    case "payroll.approve": {
      requireOwner(context);
      const candidate = state.compensation.find(
        (item) => item.id === command.candidateId,
      );
      if (!candidate)
        throw new DomainError("NOT_FOUND", "수당 후보를 찾지 못했어요.");
      candidate.state = "approved";
      candidate.approvedAt = context.now;
      return finish(
        state,
        command,
        context,
        [candidate.id],
        "수업 사건과 규칙을 대조한 후보로 승인했어요. 송금은 실행하지 않았습니다.",
      );
    }

    case "close.complete": {
      requireOwnerLike(context);
      const critical = unresolvedExceptions(state).filter(
        (item) => item.severity === "critical",
      );
      if (critical.length > 0)
        throw new DomainError(
          "CRITICAL_OPEN",
          `중요 항목 ${critical.length}건을 먼저 처리해야 마감할 수 있어요.`,
        );
      const remaining = unresolvedExceptions(state);
      if (remaining.length > 0)
        throw new DomainError(
          "EXCEPTIONS_OPEN",
          `남은 일 ${remaining.length}건을 처리하거나 이유와 함께 보류해 주세요.`,
        );
      const previous = state.closeSnapshots.filter(
        (item) =>
          item.period === command.period &&
          item.periodKey === command.periodKey,
      );
      const id = `close-${command.period}-${command.periodKey}-${previous.length + 1}`;
      state.closeSnapshots.push({
        id,
        period: command.period,
        periodKey: command.periodKey,
        revision: previous.length + 1,
        completedAt: context.now,
        reopenedAt: null,
        reopenReason: null,
        counts: {
          sessions: state.sessions.filter(
            (item) =>
              item.state === "completed" || item.state === "in_progress",
          ).length,
          students: state.attendance.filter(
            (item) => item.status !== "unmarked",
          ).length,
          messages: state.guardianDrafts.length,
          makeups: state.makeups.length,
          billingDifferences: state.invoices.filter(
            (item) => item.balanceKrw !== 0,
          ).length,
          deferred: state.exceptions.filter(
            (item) => item.status === "deferred",
          ).length,
        },
        unresolvedIds: state.exceptions
          .filter((item) => item.status === "deferred")
          .map((item) => item.id),
      });
      if (
        command.period === "day" &&
        state.tenant.mode === "workspace" &&
        state.trial.firstRealCloseAt === null
      )
        state.trial.firstRealCloseAt = context.now;
      const effectId = `effect-${context.eventId}`;
      state.effectReceipts.push({
        id: effectId,
        effectType: "close",
        subjectId: id,
        state: "accepted",
        provider: "product",
        summary: "마감 snapshot과 revision을 저장했어요.",
        occurredAt: context.now,
      });
      changed.push(id, effectId);
      return finish(
        state,
        command,
        context,
        changed,
        command.period === "day"
          ? "오늘 운영을 마쳤어요."
          : `${command.period} 마감을 저장했어요.`,
      );
    }

    case "close.reopen": {
      requireOwnerLike(context);
      if (command.reason.trim().length < 4)
        throw new DomainError(
          "REASON_REQUIRED",
          "다시 여는 이유를 4자 이상 적어 주세요.",
        );
      const snapshot = state.closeSnapshots.find(
        (item) => item.id === command.snapshotId,
      );
      if (!snapshot)
        throw new DomainError("NOT_FOUND", "마감 영수증을 찾지 못했어요.");
      if (snapshot.reopenedAt)
        throw new DomainError("ALREADY_REOPENED", "이미 다시 연 마감이에요.");
      snapshot.reopenedAt = context.now;
      snapshot.reopenReason = command.reason.trim();
      const exceptionId = `exception-reopen-${context.eventId}`;
      state.exceptions.push({
        id: exceptionId,
        kind: "attendance",
        title: "다시 연 마감 확인",
        summary: command.reason.trim(),
        studentId: null,
        sourceEventId: context.eventId,
        status: "open",
        severity: "normal",
        cause: "완료된 마감을 다시 열었어요.",
        rule: "과거 영수증은 유지하고 새 revision으로 다시 마감해요.",
        impact: "변경된 항목이 다음 마감 영수증에 반영돼요.",
        suggestedAction: "수정할 기록을 처리한 뒤 다시 마감하세요.",
        dueAt: context.now,
        revision: 1,
      });
      return finish(
        state,
        command,
        context,
        [snapshot.id, exceptionId],
        "과거 영수증은 유지하고 새 마감 revision을 열었어요.",
      );
    }

    case "support.create": {
      const risky = [
        "billing",
        "privacy",
        "security",
        "data_loss",
        "child",
        "access",
        "legal",
      ].includes(command.category);
      const redacted = redactSupportText(command.body);
      const redactedSubject = redactSupportText(command.subject).slice(0, 120);
      if (command.subject.trim().length < 3 || redacted.trim().length < 5)
        throw new DomainError(
          "SUPPORT_CONTENT_REQUIRED",
          "문의 제목과 상황을 조금 더 적어 주세요.",
        );
      const severity: SupportCase["severity"] = [
        "security",
        "data_loss",
        "privacy",
      ].includes(command.category)
        ? "P0"
        : risky
          ? "P1"
          : command.category === "bug"
            ? "P2"
            : "P3";
      const id = `support-${context.eventId}`;
      state.supportCases.push({
        id,
        category: command.category,
        severity,
        subject: redactedSubject.trim(),
        redactedBody: redacted.trim(),
        route: command.route,
        requestId: command.requestId,
        state: risky ? "human_required" : "pending_delivery",
        providerState: "not_applicable",
        createdAt: context.now,
        updatedAt: context.now,
        clusterKey: `${command.category}:${redactedSubject.trim().toLocaleLowerCase("ko-KR").slice(0, 40)}`,
      });
      return finish(
        state,
        command,
        context,
        [id],
        risky
          ? "사람이 직접 확인해야 하는 문의로 접수했어요."
          : "문의가 제품 안에 먼저 저장됐어요. 전달 상태를 여기서 확인할 수 있어요.",
      );
    }

    case "support.resolve": {
      requireOwnerLike(context);
      const supportCase = state.supportCases.find(
        (item) => item.id === command.caseId,
      );
      if (!supportCase)
        throw new DomainError("NOT_FOUND", "문의 기록을 찾지 못했어요.");
      if (command.note.trim().length < 4)
        throw new DomainError(
          "RESOLUTION_NOTE_REQUIRED",
          "처리 내용을 4자 이상 적어 주세요.",
        );
      supportCase.state = "resolved";
      supportCase.updatedAt = context.now;
      return finish(
        state,
        command,
        context,
        [supportCase.id],
        "문의 처리 내용을 기록했어요. 공급자 전달 완료와는 구분됩니다.",
      );
    }

    case "student.add": {
      requireOwnerLike(context);
      const displayName = command.displayName.trim().replace(/\s+/g, " ");
      if (displayName.length < 2 || displayName.length > 40)
        throw new DomainError(
          "STUDENT_NAME_INVALID",
          "학생 이름은 2–40자로 적어 주세요.",
        );
      const duplicate = state.students.find(
        (item) =>
          normalized(item.displayName) === normalized(displayName) &&
          item.guardianContactMasked === command.guardianContactMasked.trim(),
      );
      if (duplicate)
        throw new DomainError(
          "DUPLICATE_STUDENT",
          "같은 이름과 보호자 연락처 표식의 학생이 이미 있어요.",
        );
      const id = `student-${context.eventId}`;
      state.students.push({
        id,
        displayName,
        schoolGrade: command.schoolGrade.trim() || "미지정",
        guardianLabel: "보호자",
        guardianContactMasked:
          command.guardianContactMasked.trim() || "연락처 미등록",
        active: true,
        billingPlan: command.billingPlan,
        remainingSessions:
          command.billingPlan === "session"
            ? (command.remainingSessions ?? 0)
            : null,
        classIds: [],
      });
      return finish(
        state,
        command,
        context,
        [id],
        `${displayName} 학생을 추가했어요. 반 배정 전 상태입니다.`,
      );
    }

    case "class.create": {
      requireOwnerLike(context);
      if (
        !Number.isSafeInteger(command.capacity) ||
        command.capacity < 1 ||
        command.capacity > 300
      )
        throw new DomainError(
          "CAPACITY_INVALID",
          "정원은 1–300명으로 적어 주세요.",
        );
      if (
        !/^([01]\d|2[0-3]):[0-5]\d$/.test(command.startTime) ||
        !/^([01]\d|2[0-3]):[0-5]\d$/.test(command.endTime) ||
        command.startTime >= command.endTime
      ) {
        throw new DomainError(
          "TIME_RANGE_INVALID",
          "시작 시각은 종료 시각보다 빨라야 해요.",
        );
      }
      const weekdays = [...new Set(command.weekdays)].sort((a, b) => a - b);
      if (
        weekdays.length === 0 ||
        weekdays.some((day) => !Number.isSafeInteger(day) || day < 0 || day > 6)
      )
        throw new DomainError(
          "WEEKDAY_INVALID",
          "수업 요일을 하나 이상 골라 주세요.",
        );
      const conflict = state.classes.find(
        (item) =>
          item.weekdays.some((day) => weekdays.includes(day)) &&
          timeOverlaps(
            item.startTime,
            item.endTime,
            command.startTime,
            command.endTime,
          ) &&
          (item.room === command.room.trim() ||
            item.instructorId === command.instructorId.trim()),
      );
      if (conflict)
        throw new DomainError(
          "SCHEDULE_CONFLICT",
          `${conflict.name}과 강의실 또는 강사 시간이 겹쳐 저장하지 않았어요.`,
          false,
          { startTime: `${conflict.startTime}–${conflict.endTime} 충돌` },
        );
      const id = `class-${context.eventId}`;
      const classGroup: WorkspaceState["classes"][number] = {
        id,
        name: command.name.trim(),
        subject: command.subject.trim(),
        instructorId: command.instructorId.trim(),
        instructorName: command.instructorName.trim(),
        room: command.room.trim(),
        capacity: command.capacity,
        weekdays,
        startTime: command.startTime,
        endTime: command.endTime,
        compensationKrw: command.compensationKrw,
        studentIds: [],
      };
      state.classes.push(classGroup);
      const occurrences = upcomingOccurrences(
        context.now,
        state.tenant.timezone,
        weekdays,
        command.startTime,
        command.endTime,
      );
      for (const occurrence of occurrences) {
        const sessionId = `session-${id}-${occurrence.dateKey}`;
        state.sessions.push({
          id: sessionId,
          classId: id,
          startsAt: occurrence.startsAt,
          endsAt: occurrence.endsAt,
          state: occurrence.state,
          revision: 1,
        });
        changed.push(sessionId);
      }
      return finish(
        state,
        command,
        context,
        [id, ...changed],
        `${command.name.trim()} 수업과 앞으로 8주 일정 ${occurrences.length}개를 만들었어요.`,
      );
    }

    case "class.rosterUpdate": {
      requireOwnerLike(context);
      const classGroup = state.classes.find(
        (item) => item.id === command.classId,
      );
      if (!classGroup)
        throw new DomainError("NOT_FOUND", "수업을 찾지 못했어요.");
      const nextIds = [...new Set(command.studentIds)];
      if (nextIds.length > classGroup.capacity)
        throw new DomainError(
          "CAPACITY_FULL",
          `정원 ${classGroup.capacity}명을 넘겨 명단을 저장하지 않았어요.`,
        );
      if (
        nextIds.some(
          (studentId) =>
            !state.students.some(
              (student) => student.id === studentId && student.active,
            ),
        )
      )
        throw new DomainError(
          "NOT_FOUND",
          "명단에서 확인할 수 없는 학생이 있어 저장하지 않았어요.",
        );
      const before = new Set(classGroup.studentIds);
      const after = new Set(nextIds);
      classGroup.studentIds = nextIds;
      changed.push(classGroup.id);
      for (const student of state.students) {
        if (after.has(student.id) && !student.classIds.includes(classGroup.id)) {
          student.classIds.push(classGroup.id);
          changed.push(student.id);
        } else if (!after.has(student.id) && before.has(student.id)) {
          student.classIds = student.classIds.filter(
            (classId) => classId !== classGroup.id,
          );
          changed.push(student.id);
        }
      }
      const activeSessions = state.sessions.filter(
        (session) =>
          session.classId === classGroup.id &&
          ["scheduled", "in_progress"].includes(session.state),
      );
      for (const session of activeSessions) {
        state.attendance = state.attendance.filter(
          (record) =>
            !(
              record.sessionId === session.id &&
              record.status === "unmarked" &&
              !after.has(record.studentId)
            ),
        );
        for (const studentId of nextIds) {
          if (
            state.attendance.some(
              (record) =>
                record.sessionId === session.id &&
                record.studentId === studentId,
            )
          )
            continue;
          const attendanceId = `attendance-${session.id}-${studentId}`;
          state.attendance.push({
            id: attendanceId,
            sessionId: session.id,
            studentId,
            status: "unmarked",
            recordedAt: null,
            source: "server",
            syncState: "idle",
            version: 1,
          });
          changed.push(attendanceId);
        }
      }
      return finish(
        state,
        command,
        context,
        changed,
        `${classGroup.name} 명단을 ${nextIds.length}명으로 저장했어요. 예정 출결표에도 반영했습니다.`,
      );
    }

    case "team.invitationPrepare": {
      requireOwner(context);
      if (command.role === "owner")
        throw new DomainError(
          "OWNER_TRANSFER_REAUTH_REQUIRED",
          "원장 권한 이전은 재인증과 양쪽 확인이 필요해 이 화면에서 준비할 수 없어요.",
        );
      const id = `member-${context.eventId}`;
      state.teamMembers.push({
        id,
        displayName: command.displayName.trim(),
        role: command.role,
        scopeClassIds: [...new Set(command.scopeClassIds)],
        state: "invitation_prepared",
        invitationReceipt: "draft",
        createdAt: context.now,
        updatedAt: context.now,
      });
      return finish(
        state,
        command,
        context,
        [id],
        "권한 초안을 저장했어요. 이메일 초대는 보내지 않았습니다.",
      );
    }

    case "team.roleChange": {
      requireOwner(context);
      const member = state.teamMembers.find(
        (item) => item.id === command.memberId,
      );
      if (!member)
        throw new DomainError("NOT_FOUND", "구성원을 찾지 못했어요.");
      if (member.role === "owner" || command.role === "owner")
        throw new DomainError(
          "OWNER_TRANSFER_REAUTH_REQUIRED",
          "원장 권한은 이 변경 흐름에서 넘길 수 없어요.",
        );
      member.role = command.role;
      member.scopeClassIds = [...new Set(command.scopeClassIds)];
      member.updatedAt = context.now;
      return finish(
        state,
        command,
        context,
        [member.id],
        `${member.displayName}의 역할과 수업 범위를 바꿨어요.`,
      );
    }

    case "import.students": {
      requireOwnerLike(context);
      if (command.rows.length === 0)
        throw new DomainError("IMPORT_EMPTY", "가져올 정상 행이 없어요.");
      if (command.rows.length > 500)
        throw new DomainError(
          "IMPORT_TOO_LARGE",
          "한 번에 500명까지 가져올 수 있어요. 파일을 나눠 주세요.",
        );
      const seen = new Set(
        state.students.map(
          (item) =>
            `${normalized(item.displayName)}|${normalized(item.guardianContactMasked)}`,
        ),
      );
      const insertedStudentIds: string[] = [];
      let duplicateRows = 0;
      command.rows.forEach((row, index) => {
        const key = `${normalized(row.displayName)}|${normalized(row.guardianContactMasked)}`;
        if (seen.has(key)) {
          duplicateRows += 1;
          return;
        }
        seen.add(key);
        const id = `student-${context.eventId}-${index + 1}`;
        state.students.push({
          id,
          displayName: row.displayName.trim().replace(/\s+/g, " "),
          schoolGrade: row.schoolGrade.trim() || "미지정",
          guardianLabel: "보호자",
          guardianContactMasked:
            row.guardianContactMasked.trim() || "연락처 미등록",
          active: true,
          billingPlan: row.billingPlan,
          remainingSessions:
            row.billingPlan === "session" ? (row.remainingSessions ?? 0) : null,
          classIds: [],
        });
        insertedStudentIds.push(id);
      });
      const receiptId = `import-${context.eventId}`;
      state.importReceipts.push({
        id: receiptId,
        fileName: command.fileName.trim().slice(0, 120),
        acceptedRows: insertedStudentIds.length,
        rejectedRows: command.rejectedRows,
        duplicateRows,
        insertedStudentIds,
        state: "committed",
        createdAt: context.now,
        rolledBackAt: null,
      });
      return finish(
        state,
        command,
        context,
        [receiptId, ...insertedStudentIds],
        `${insertedStudentIds.length}명을 가져왔어요. 중복 ${duplicateRows}행은 건너뛰고 영수증에 남겼습니다.`,
      );
    }

    case "import.rollback": {
      requireOwner(context);
      const receipt = state.importReceipts.find(
        (item) => item.id === command.receiptId,
      );
      if (!receipt)
        throw new DomainError("NOT_FOUND", "가져오기 영수증을 찾지 못했어요.");
      if (receipt.state === "rolled_back")
        return finish(
          state,
          command,
          context,
          [receipt.id],
          "이미 되돌린 가져오기예요.",
        );
      const referenced = receipt.insertedStudentIds.find(
        (studentId) =>
          state.classes.some((item) => item.studentIds.includes(studentId)) ||
          state.ledger.some((item) => item.studentId === studentId),
      );
      if (referenced)
        throw new DomainError(
          "IMPORT_ROLLBACK_CONFLICT",
          "가져온 학생 중 운영 기록이 생긴 학생이 있어 자동으로 되돌릴 수 없어요. 지원에 복구를 요청해 주세요.",
        );
      const targets = new Set(receipt.insertedStudentIds);
      state.students = state.students.filter((item) => !targets.has(item.id));
      receipt.state = "rolled_back";
      receipt.rolledBackAt = context.now;
      return finish(
        state,
        command,
        context,
        [receipt.id, ...receipt.insertedStudentIds],
        `${receipt.insertedStudentIds.length}명의 가져오기를 되돌렸어요.`,
      );
    }

    case "guardian.draftApprove": {
      requireOwnerLike(context);
      const draft = state.guardianDrafts.find(
        (item) => item.id === command.draftId,
      );
      if (!draft)
        throw new DomainError("NOT_FOUND", "보호자 안내 초안을 찾지 못했어요.");
      if (draft.quietHoursBlocked)
        throw new DomainError(
          "QUIET_HOURS",
          "보호자 수신 휴식 시간이라 지금은 승인할 수 없어요.",
        );
      draft.requiresReview = false;
      draft.receiptState = "accepted";
      draft.updatedAt = context.now;
      const effectId = `effect-${context.eventId}`;
      state.effectReceipts.push({
        id: effectId,
        effectType: "message",
        subjectId: draft.id,
        state: "not_applicable",
        provider: "disabled",
        summary:
          "내용 검토만 완료했습니다. 메시지 공급자가 꺼져 있어 발송하지 않았어요.",
        occurredAt: context.now,
      });
      return finish(
        state,
        command,
        context,
        [draft.id, effectId],
        "보호자 안내 내용을 확인했어요. 실제 발송은 하지 않았습니다.",
      );
    }

    case "guardian.capabilityAttach": {
      requireOwnerLike(context);
      const draft = state.guardianDrafts.find(
        (item) => item.id === command.draftId,
      );
      if (!draft)
        throw new DomainError("NOT_FOUND", "보호자 안내 초안을 찾지 못했어요.");
      draft.capabilityId = command.capabilityId;
      draft.updatedAt = context.now;
      return finish(
        state,
        command,
        context,
        [draft.id, command.capabilityId],
        "목적과 만료가 제한된 보호자 확인 링크를 만들었어요. 메시지는 보내지 않았습니다.",
      );
    }

    case "data.deletionRequest": {
      requireOwner(context);
      if (normalized(command.confirmation) !== normalized(state.tenant.name))
        throw new DomainError(
          "CONFIRMATION_MISMATCH",
          "삭제 요청을 받으려면 현재 학원 이름을 정확히 적어 주세요.",
        );
      if (
        state.dataRequests.some(
          (item) => item.kind === "deletion" && item.state === "received",
        )
      )
        throw new DomainError(
          "DELETION_ALREADY_REQUESTED",
          "이미 처리 대기 중인 삭제 요청이 있어요.",
        );
      const requestedAt = new Date(context.now);
      const scheduledFor = new Date(
        requestedAt.getTime() + 30 * 86_400_000,
      ).toISOString();
      const id = `deletion-${context.eventId}`;
      state.dataRequests.push({
        id,
        kind: "deletion",
        state: "received",
        requestedAt: context.now,
        scheduledFor,
        completedAt: null,
        legalHoldNotice:
          "법령상 보존이 필요한 결제·계약 기록은 해당 기간 뒤 파기되며, 그 외 운영 데이터는 예정일에 삭제합니다.",
      });
      return finish(
        state,
        command,
        context,
        [id],
        "삭제 요청을 제품 안에 저장했어요. 예정일 전에는 취소할 수 있습니다.",
      );
    }

    case "data.deletionCancel": {
      requireOwner(context);
      const request = state.dataRequests.find(
        (item) => item.id === command.requestId && item.kind === "deletion",
      );
      if (!request)
        throw new DomainError("NOT_FOUND", "삭제 요청을 찾지 못했어요.");
      if (request.state !== "received")
        throw new DomainError(
          "DELETION_NOT_CANCELLABLE",
          "현재 상태에서는 삭제 요청을 취소할 수 없어요.",
        );
      request.state = "cancelled";
      return finish(
        state,
        command,
        context,
        [request.id],
        "삭제 요청을 취소했어요. 데이터는 그대로 유지됩니다.",
      );
    }

    case "trial.planIntent": {
      requireOwner(context);
      state.trial.plan = command.plan;
      state.trial.providerState = "disabled";
      return finish(
        state,
        command,
        context,
        ["trial"],
        `${command.plan} 요금제 관심을 저장했어요. 결제나 구독은 시작하지 않았습니다.`,
      );
    }

    case "preferences.update": {
      if (command.reducedMotion !== undefined)
        state.preferences.reducedMotion = command.reducedMotion;
      if (command.highContrast !== undefined)
        state.preferences.highContrast = command.highContrast;
      return finish(
        state,
        command,
        context,
        ["preferences"],
        "화면 표시 설정을 저장했어요.",
      );
    }
  }
}
